import { useEffect, useRef } from "react";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { bbox, childOrigin, shape } from "./dieline";

const ROT = { "+x": ["y", 1], "-x": ["y", -1], "+y": ["x", -1], "-y": ["x", 1] };

function buildGroup(n, dir, len, fold, mat, lineMat, hinges) {
  const g = new THREE.Group();
  const pts = shape(dir, len, n.depth, n.taper);
  const s = new THREE.Shape(pts.map(([x, y]) => new THREE.Vector2(x, y)));
  const geo = new THREE.ShapeGeometry(s);
  g.add(new THREE.Mesh(geo, mat));
  g.add(new THREE.LineLoop(new THREE.BufferGeometry().setFromPoints(pts.map(([x, y]) => new THREE.Vector3(x, y, 0))), lineMat));
  const box = bbox(dir, len, n.depth);
  Object.entries(n.children).forEach(([edge, c]) => {
    const o = childOrigin(edge, box);
    const pivot = new THREE.Group();
    pivot.position.set(o.at[0], o.at[1], 0);
    pivot.add(buildGroup(c, o.dir, o.len, fold, mat, lineMat, hinges));
    const [axis, sign] = ROT[o.dir];
    hinges.push({ pivot, axis, max: (c.angle * Math.PI) / 180 * sign });
    g.add(pivot);
  });
  return g;
}

export function Dieline3D({ model, fold, size }) {
  const mount = useRef(null);
  const state = useRef({});
  useEffect(() => {
    const el = mount.current;
    const w = el.clientWidth, h = 360;
    const scene = new THREE.Scene();
    scene.background = new THREE.Color("#F8FAFC");
    const camera = new THREE.PerspectiveCamera(40, w / h, 1, 20000);
    const renderer = new THREE.WebGLRenderer({ antialias: true, preserveDrawingBuffer: true });
    renderer.setSize(w, h);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    el.appendChild(renderer.domElement);
    scene.add(new THREE.AmbientLight(0xffffff, 0.75));
    const dl = new THREE.DirectionalLight(0xffffff, 0.9);
    dl.position.set(1, 2, 3);
    scene.add(dl);
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    let raf;
    const loop = () => { controls.update(); renderer.render(scene, camera); raf = requestAnimationFrame(loop); };
    loop();
    state.current = { scene, camera, controls };
    return () => { cancelAnimationFrame(raf); controls.dispose(); renderer.dispose(); el.removeChild(renderer.domElement); };
  }, []);

  useEffect(() => {
    const { scene, camera, controls } = state.current;
    if (!scene || !model) return;
    if (state.current.root) scene.remove(state.current.root);
    const mat = new THREE.MeshStandardMaterial({ color: "#D6B98C", side: THREE.DoubleSide, roughness: 0.85 });
    const lineMat = new THREE.LineBasicMaterial({ color: "#7C5A2E" });
    const hinges = [];
    const inner = buildGroup(model, "+y", model.len, fold, mat, lineMat, hinges);
    const root = new THREE.Group();
    root.add(inner);
    scene.add(root);
    state.current = { ...state.current, root, inner, hinges };
    const r = Math.max(...size) * 1.2;
    camera.position.set(r * 1.4, r * 1.1, r * 1.9);
    controls.target.set(size[0] / 2, size[2] / 2, -size[1] / 2);
    camera.lookAt(controls.target);
  }, [model]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    (state.current.hinges || []).forEach(({ pivot, axis, max }) => { pivot.rotation[axis] = max * fold; });
  }, [fold, model]);

  return <div ref={mount} data-testid="dieline-3d-canvas" style={{ width: "100%", height: 360, borderRadius: 12, overflow: "hidden", border: "1px solid #E2E8F0", cursor: "grab" }}/>;
}
